/** The live-session socket, shared by student, teacher and projector surfaces.
 *
 * Reconnects with backoff; every incoming `state` snapshot fully replaces the local view (the
 * server always sends the whole snapshot for the connection's audience, so there is nothing
 * to merge). Outgoing messages are queued while disconnected and flushed on reconnect, so an
 * answer tapped during a Wi-Fi blip is not lost; the server de-duplicates by (device, seq). */
import { wsUrl } from "../api/base";

export type ServerMessage = {
  kind: "state" | "item_open" | "item_closed" | "aggregate" | "response_ack" | "suggestion" | "revealed" | "ended" | "error" | "pong";
  session_id: string;
  data: Record<string, unknown>;
};

export type ConnState = "live" | "reconnecting" | "offline";

/** Close codes after which the socket stops instead of reconnecting: 4401 no credential for
 * this session, 4403 a staff token outside the course's reach, 4404 an unknown session or a
 * course the server does not know yet (STORY-141). */
export const FINAL_CLOSE_CODES: ReadonlySet<number> = new Set([4401, 4403, 4404]);

export type LiveSocketOptions = {
  sessionId: string;
  token: string;
  deviceId: string;
  onMessage: (m: ServerMessage) => void;
  onState: (s: ConnState) => void;
  /** HTTP fallback for a payload queued while the socket is down (STORY-030). Resolves with
   * the server's acknowledgement (then the payload leaves the queue) or null to keep it queued. */
  fallback?: (payload: Record<string, unknown>) => Promise<ServerMessage | null>;
};

export class LiveSocket {
  private ws: WebSocket | null = null;
  private queue: string[] = [];
  private attempts = 0;
  private closed = false;
  private timer: number | null = null;
  private readonly opts: LiveSocketOptions;

  constructor(opts: LiveSocketOptions) {
    this.opts = opts;
  }

  connect(): void {
    if (this.closed) return;
    const url = `${wsUrl(`/session/v1/ws/${this.opts.sessionId}`)}?token=${encodeURIComponent(this.opts.token)}&device=${encodeURIComponent(this.opts.deviceId)}`;
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => {
      this.attempts = 0;
      this.opts.onState("live");
      for (const m of this.queue.splice(0)) ws.send(m);
    };
    ws.onmessage = (ev) => {
      try {
        this.opts.onMessage(JSON.parse(String(ev.data)) as ServerMessage);
      } catch {
        /* ignore malformed frames */
      }
    };
    ws.onclose = (ev) => {
      this.ws = null;
      if (this.closed || FINAL_CLOSE_CODES.has(ev.code)) {
        this.opts.onState("offline");
        return;
      }
      this.opts.onState("reconnecting");
      const delay = Math.min(8000, 500 * 2 ** this.attempts++);
      this.timer = window.setTimeout(() => this.connect(), delay);
    };
    ws.onerror = () => ws.close();
  }

  send(payload: Record<string, unknown>): void {
    const text = JSON.stringify(payload);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(text);
      return;
    }
    this.queue.push(text);
    const fb = this.opts.fallback;
    if (fb) {
      void fb(payload).then((ack) => {
        if (!ack) return;
        const i = this.queue.indexOf(text);
        if (i >= 0) this.queue.splice(i, 1);
        this.opts.onMessage(ack);
      });
    }
  }

  close(): void {
    this.closed = true;
    if (this.timer) window.clearTimeout(this.timer);
    this.ws?.close();
  }
}
